import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import { PageHeader } from "../components/PageHeader";
import { RetryButton, StatePanel } from "../components/StatePanel";
import { StatusChip } from "../components/StatusChip";
import { api, mutationKey } from "../lib/api";
import type { ListResponse, RequestItem } from "../lib/types";

interface ReferenceData {
  readonly organizations?: readonly {
    readonly id: string;
    readonly name: string;
  }[];
  readonly municipalities: readonly {
    readonly id: string;
    readonly name: string;
  }[];
  readonly sites: readonly {
    readonly id: string;
    readonly municipality_id: string;
    readonly name: string;
  }[];
  readonly plant_types: readonly {
    readonly id: string;
    readonly common_name: string;
  }[];
}

const terminalRequestStates = new Set(["cancelled", "completed"]);

function dateInQuezon(now: Date): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Manila",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const value = Object.fromEntries(
    parts.map((part) => [part.type, part.value]),
  );
  return `${value.year ?? ""}-${value.month ?? ""}-${value.day ?? ""}`;
}

export function isRequestOverdue(request: RequestItem, now = new Date()) {
  return (
    !terminalRequestStates.has(request.state) &&
    request.target_date < dateInQuezon(now)
  );
}

export function RequestsPage() {
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState<RequestItem | null>(null);
  const queryClient = useQueryClient();
  const requests = useQuery({
    queryKey: ["requests"],
    queryFn: () => api<ListResponse<RequestItem>>("/planting-requests"),
  });
  const reference = useQuery({
    queryKey: ["reference"],
    queryFn: () => api<ReferenceData>("/reference-data"),
  });
  const decide = useMutation({
    mutationFn: ({
      request,
      decision,
    }: {
      readonly request: RequestItem;
      readonly decision: "approved" | "returned" | "cancelled";
    }) =>
      api(`/planting-requests/${request.id}/decision`, {
        method: "POST",
        idempotencyKey: mutationKey("request-decision"),
        body: JSON.stringify({
          decision,
          reason: `Staff selected ${decision.replaceAll("_", " ")}.`,
          expected_resource_version: request.version,
        }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["requests"] });
    },
  });
  return (
    <>
      <PageHeader
        eyebrow="Manager workspace"
        title="Planting requests"
        description="Record need first, then approve and assign responsibility within your scope."
        actions={
          <button
            className="button primary"
            type="button"
            onClick={() => setCreating(true)}
          >
            <Plus aria-hidden="true" />
            New request
          </button>
        }
      />
      {creating ? (
        <RequestForm
          reference={reference.data}
          onClose={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            await queryClient.invalidateQueries({ queryKey: ["requests"] });
          }}
        />
      ) : null}
      {assigning === null ? null : (
        <AssignmentForm
          request={assigning}
          onClose={() => setAssigning(null)}
          onAssigned={async () => {
            setAssigning(null);
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: ["requests"] }),
              queryClient.invalidateQueries({ queryKey: ["assignments"] }),
            ]);
          }}
        />
      )}
      {requests.isLoading ? (
        <StatePanel
          kind="loading"
          title="Loading requests"
          detail="Reading the current request register."
        />
      ) : requests.isError || requests.data === undefined ? (
        <StatePanel
          kind="error"
          title="Requests are unavailable"
          detail="The request register could not be loaded."
          action={<RetryButton onClick={() => void requests.refetch()} />}
        />
      ) : requests.data.items.length === 0 ? (
        <StatePanel
          kind="empty"
          title="No planting requests"
          detail="Create a request before assigning a planter and post allowance."
        />
      ) : (
        <div className="table-wrap">
          <table>
            <caption className="sr-only">Planting requests</caption>
            <thead>
              <tr>
                <th scope="col">Request</th>
                <th scope="col">Site</th>
                <th scope="col">Quantity</th>
                <th scope="col">Target</th>
                <th scope="col">State</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.data.items.map((request) => (
                <tr key={request.id}>
                  <td>
                    <strong className="record-code">
                      {request.public_code}
                    </strong>
                    <small>{request.requestor_name}</small>
                  </td>
                  <td>
                    {request.site_name}
                    <small>{request.organization_name}</small>
                  </td>
                  <td>{request.requested_quantity.toLocaleString()}</td>
                  <td>{request.target_date}</td>
                  <td>
                    <div className="status-stack">
                      <StatusChip state={request.state} />
                      {isRequestOverdue(request) ? (
                        <StatusChip state="Overdue" />
                      ) : null}
                    </div>
                  </td>
                  <td>
                    <div className="row-actions">
                      {request.state === "draft" ||
                      request.state === "returned" ? (
                        <button
                          className="text-button"
                          type="button"
                          disabled={decide.isPending}
                          onClick={() =>
                            decide.mutate({ request, decision: "approved" })
                          }
                        >
                          Approve
                        </button>
                      ) : null}
                      {request.state === "draft" ? (
                        <button
                          className="text-button danger-text"
                          type="button"
                          disabled={decide.isPending}
                          onClick={() =>
                            decide.mutate({ request, decision: "cancelled" })
                          }
                        >
                          Cancel
                        </button>
                      ) : null}
                      {request.state === "approved" ? (
                        <button
                          className="text-button"
                          type="button"
                          onClick={() => setAssigning(request)}
                        >
                          Assign
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {decide.isError ? (
        <p className="form-error" role="alert">
          {decide.error.message}
        </p>
      ) : null}
    </>
  );
}

interface AssignmentCandidates {
  readonly planters: readonly {
    readonly id: string;
    readonly display_name: string;
  }[];
  readonly managers: readonly {
    readonly id: string;
    readonly display_name: string;
  }[];
}

function AssignmentForm({
  request,
  onClose,
  onAssigned,
}: {
  readonly request: RequestItem;
  readonly onClose: () => void;
  readonly onAssigned: () => Promise<void>;
}) {
  const candidates = useQuery({
    queryKey: ["assignment-candidates", request.site_id],
    queryFn: () =>
      api<AssignmentCandidates>(
        `/assignment-candidates?site_id=${encodeURIComponent(request.site_id)}`,
      ),
  });
  const assign = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/planting-requests/${request.id}/assignments`, {
        method: "POST",
        idempotencyKey: mutationKey("assignment-create"),
        body: JSON.stringify(body),
      }),
    onSuccess: onAssigned,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const startsAt = data.get("starts_at");
    const endsAt = data.get("ends_at");
    if (typeof startsAt !== "string") return;
    assign.mutate({
      primary_planter_id: data.get("primary_planter_id"),
      responsible_manager_id: data.get("responsible_manager_id"),
      site_id: request.site_id,
      declared_plant_quantity: Number(data.get("declared_plant_quantity")),
      starts_at: new Date(startsAt).toISOString(),
      ...(typeof endsAt === "string" && endsAt !== ""
        ? { ends_at: new Date(endsAt).toISOString() }
        : {}),
      initial_post_allowance: Number(data.get("initial_post_allowance")),
      reason: data.get("reason"),
    });
  };
  return (
    <div className="dialog-layer" role="presentation">
      <section
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="assignment-form-title"
      >
        <div className="dialog-title">
          <div>
            <p className="eyebrow">{request.public_code}</p>
            <h2 id="assignment-form-title">Assign planting</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label="Close assignment form"
            onClick={onClose}
          >
            <X aria-hidden="true" />
          </button>
        </div>
        {candidates.isLoading ? (
          <StatePanel
            kind="loading"
            title="Loading eligible people"
            detail="Checking active membership and manager scope."
          />
        ) : candidates.isError || candidates.data === undefined ? (
          <StatePanel
            kind="error"
            title="Candidates unavailable"
            detail="No assignment was created."
            action={<RetryButton onClick={() => void candidates.refetch()} />}
          />
        ) : (
          <form onSubmit={submit}>
            <label>
              Primary planter
              <select name="primary_planter_id" required>
                <option value="">Choose an active planter</option>
                {candidates.data.planters.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.display_name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Responsible manager
              <select name="responsible_manager_id" required>
                <option value="">Choose a scoped manager</option>
                {candidates.data.managers.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.display_name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Declared plant quantity
              <input
                name="declared_plant_quantity"
                type="number"
                min="1"
                max="1000000"
                defaultValue={request.requested_quantity}
                required
              />
            </label>
            <label>
              Active from
              <input
                name="starts_at"
                type="datetime-local"
                defaultValue={new Date().toISOString().slice(0, 16)}
                required
              />
            </label>
            <label>
              Active until (optional)
              <input name="ends_at" type="datetime-local" />
              <small>
                Posting closes at this time. Leave blank for no scheduled end.
              </small>
            </label>
            <label>
              Initial post allowance
              <input
                name="initial_post_allowance"
                type="number"
                min="1"
                max="10000"
                defaultValue="4"
                required
              />
              <small>The active server policy validates the final cap.</small>
            </label>
            <label>
              Assignment reason
              <textarea
                name="reason"
                minLength={3}
                maxLength={500}
                defaultValue="Approved planting responsibility assigned"
                required
              />
            </label>
            {assign.isError ? (
              <p className="form-error" role="alert">
                {assign.error.message}
              </p>
            ) : null}
            <div className="dialog-actions">
              <button
                className="button secondary"
                type="button"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="button primary"
                type="submit"
                disabled={assign.isPending}
              >
                {assign.isPending ? "Assigning…" : "Create assignment"}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}

function RequestForm({
  reference,
  onClose,
  onCreated,
}: {
  readonly reference: ReferenceData | undefined;
  readonly onClose: () => void;
  readonly onCreated: () => Promise<void>;
}) {
  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api("/planting-requests", {
        method: "POST",
        idempotencyKey: mutationKey("request-create"),
        body: JSON.stringify(body),
      }),
    onSuccess: onCreated,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const rawSiteId = data.get("site_id");
    const siteId = typeof rawSiteId === "string" ? rawSiteId : "";
    const site = reference?.sites.find((item) => item.id === siteId);
    create.mutate({
      requestor_name: data.get("requestor_name"),
      organization_id: data.get("organization_id"),
      municipality_id: site?.municipality_id,
      site_id: siteId,
      plant_type_ids: [data.get("plant_type_id")],
      requested_quantity: Number(data.get("requested_quantity")),
      target_date: data.get("target_date"),
      notes: data.get("notes"),
    });
  };
  return (
    <div className="dialog-layer" role="presentation">
      <section
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-form-title"
      >
        <div className="dialog-title">
          <div>
            <p className="eyebrow">New record</p>
            <h2 id="request-form-title">Planting request</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label="Close request form"
            onClick={onClose}
          >
            <X aria-hidden="true" />
          </button>
        </div>
        <form onSubmit={submit}>
          <label>
            Requestor name
            <input name="requestor_name" required minLength={2} />
          </label>
          <label>
            Organization
            <select name="organization_id" required>
              <option value="">Choose organization</option>
              {reference?.organizations?.map((organization) => (
                <option key={organization.id} value={organization.id}>
                  {organization.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Site
            <select name="site_id" required>
              <option value="">Choose site</option>
              {reference?.sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Plant type
            <select name="plant_type_id" required>
              <option value="">Choose plant type</option>
              {reference?.plant_types.map((plant) => (
                <option key={plant.id} value={plant.id}>
                  {plant.common_name}
                </option>
              ))}
            </select>
          </label>
          <div className="field-row">
            <label>
              Requested quantity
              <input
                name="requested_quantity"
                type="number"
                required
                min={1}
                max={1_000_000}
              />
            </label>
            <label>
              Target date
              <input name="target_date" type="date" required />
            </label>
          </div>
          <label>
            Notes
            <textarea name="notes" maxLength={2_000} rows={3} />
          </label>
          {create.isError ? (
            <p className="form-error" role="alert">
              {create.error.message}
            </p>
          ) : null}
          <div className="dialog-actions">
            <button
              className="button secondary"
              type="button"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="button primary"
              type="submit"
              disabled={create.isPending}
            >
              {create.isPending ? "Saving…" : "Save request"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
